import { useEffect, useState } from "react";
import { useAuth, startLogin } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { updateMe } from "@/lib/api";
import { toast } from "@/lib/toast";
import { LogIn, LogOut, User, Loader2, Mail, Sun, Moon } from "lucide-react";

const fmt = (d) =>
  d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "America/New_York",
  });

export default function TickerHeader() {
  const [now, setNow] = useState(new Date());
  const { user, loading, logout, setUser } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);

  const setUserDigest = (val) => setUser((u) => (u ? { ...u, email_digest: val } : u));

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (!e.target.closest("[data-account-menu]")) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <header className="relative z-30 border-b border-zinc-900 bg-[#09090B]/80 backdrop-blur-sm">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="size-7 border border-zinc-700 flex items-center justify-center">
            <span className="font-mono text-[10px] text-zinc-300 font-bold">TR</span>
          </div>
          <div className="font-bold tracking-tight text-sm sm:text-base">
            TOTAL RETURN TRACKER
          </div>
        </div>
        <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          <div className="hidden md:flex items-center gap-2">
            <span className="live-dot inline-block" />
            <span className="text-zinc-300">MKT DATA</span>
          </div>
          <div className="hidden md:block">NY · {fmt(now)}</div>

          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Light mode" : "Dark mode"}
            className="inline-flex items-center justify-center border border-zinc-800 hover:border-zinc-500 hover:text-white text-zinc-300 size-7 transition-colors"
          >
            {theme === "dark" ? <Sun size={12} /> : <Moon size={12} />}
          </button>

          {loading ? (
            <Loader2 size={14} className="animate-spin text-zinc-500" />
          ) : user ? (
            <div className="relative" data-account-menu>
              <button
                data-testid="account-menu-trigger"
                onClick={() => setMenuOpen((o) => !o)}
                className="inline-flex items-center gap-2 border border-zinc-800 hover:border-zinc-500 hover:text-white text-zinc-300 px-2.5 py-1.5 transition-colors"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="size-5 object-cover" />
                ) : (
                  <User size={12} />
                )}
                <span className="max-w-[120px] truncate normal-case tracking-normal">
                  {user.name?.split(" ")[0] || user.email}
                </span>
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-1 w-64 border border-zinc-800 bg-[#0d0d0f] z-50">
                  <div className="px-3 py-2 border-b border-zinc-900">
                    <div className="text-[11px] font-mono normal-case tracking-normal text-zinc-300 truncate">
                      {user.name}
                    </div>
                    <div className="text-[10px] normal-case tracking-normal text-zinc-500 truncate">
                      {user.email}
                    </div>
                  </div>
                  <button
                    data-testid="email-digest-toggle"
                    role="switch"
                    aria-checked={!!user.email_digest}
                    aria-label="Weekly email digest"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const next = !user.email_digest;
                      try {
                        const fresh = await updateMe({ email_digest: next });
                        setUserDigest(fresh.email_digest);
                        toast.success(
                          next ? "Weekly email digest enabled" : "Weekly email digest disabled"
                        );
                      } catch {
                        toast.error("Could not update preference");
                      }
                    }}
                    className="w-full text-left px-3 py-2 flex items-center justify-between gap-3 hover:bg-zinc-900 transition-colors normal-case tracking-normal"
                  >
                    <span className="inline-flex items-center gap-2 text-zinc-300 text-xs">
                      <Mail size={12} /> Weekly email digest
                    </span>
                    <span
                      className={`w-8 h-4 border ${user.email_digest ? "bg-[#00E676] border-[#00E676]" : "bg-zinc-800 border-zinc-700"} relative transition-colors`}
                    >
                      <span
                        className={`block w-3 h-3 bg-black absolute top-[1px] transition-all ${user.email_digest ? "left-[17px]" : "left-[1px]"}`}
                      />
                    </span>
                  </button>
                  <button
                    data-testid="logout-btn"
                    onClick={() => {
                      setMenuOpen(false);
                      logout();
                    }}
                    className="w-full text-left px-3 py-2 inline-flex items-center gap-2 hover:bg-zinc-900 text-zinc-300 hover:text-white transition-colors border-t border-zinc-900 normal-case tracking-normal"
                  >
                    <LogOut size={12} /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              data-testid="login-btn"
              onClick={startLogin}
              className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-zinc-500 hover:text-white text-zinc-300 px-2.5 py-1.5 transition-colors"
            >
              <LogIn size={12} /> Sign in
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
